import React, {forwardRef} from 'react'
import styled, {css} from 'styled-components'
import {motion, MotionProps} from 'framer-motion'
import {Box, BoxProps, themeGet, useRefObjectAsForwardedRef, SxProp, sx} from '../..'

import {BoxWithIndicatorProps, BoxWithIndicatorArrowProps, Size} from './types'
/**
 * A component that renders a box with a caret indicator positioned vertically. Maintains awareness of the y-axis center of a given element in the corresponding pane of the rule builder layout.
 */
const BoxWithIndicator = forwardRef(
  (
    {
      arrowSize = 'lg',
      caretTop,
      isArrowAnimated = true,
      hideArrow = false,
      side = 'left',
      testId,
      children,
      sx: sxProp
    }: BoxWithIndicatorProps,
    forwardedRef
  ) => {
    const containerRef = React.useRef<HTMLDivElement>(null)
    useRefObjectAsForwardedRef(forwardedRef, containerRef)

    // Set the default calculatedCaretTop value to 14px for `lg` arrowSize
    let calculatedCaretTop = 14

    // Override calculatedCaretTop value if caretTop prop is provided
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (caretTop !== undefined) {
      calculatedCaretTop = caretTop
    } else {
      // For `md` arrowSize, set calculatedCaretTop to 12px
      if (arrowSize === 'md') {
        calculatedCaretTop = 12
      }
      // For `sm` arrowSize, set calculatedCaretTop to 11px
      else if (arrowSize === 'sm') {
        calculatedCaretTop = 11
      }
    }

    const caretWidth = arrowSize === 'md' ? '24px' : '32px' // Width for `md` variant with 24px legs
    const caretHeight = arrowSize === 'md' ? '24px' : '32px' // Height for `md` variant with 24px legs

    const caretTopValue = arrowSize === 'md' ? '12px' : '14px' // Top value for `md` variant with 24px legs
    const caretRightValue = arrowSize === 'md' ? '12px' : '16px' // Right value for `md` variant with 24px legs

    const arrowTestId = `${testId}-arrow`
    const wrapperTestId = `${testId}-wrapper`

    return (
      <Wrapper data-testid={testId ? wrapperTestId : undefined} ref={containerRef} sx={sxProp}>
        {hideArrow ? null : (
          <Arrow
            arrowSize={arrowSize}
            caretTop={calculatedCaretTop}
            side={side}
            initial={{opacity: 0}} // Initial opacity value
            animate={{opacity: 1, top: calculatedCaretTop}} // Opacity and top values to animate to
            transition={{
              ease: 'easeInOut', // Easing function
              duration: 0.107 // Duration in seconds
            }} // Transition configuration
            data-testid={testId ? arrowTestId : undefined}
          />
        )}
        {children}
      </Wrapper>
    )
  }
)

const Wrapper = styled(Box)<BoxProps & SxProp>`
  --border-color: ${themeGet('colors.border.default')};
  --background-color: ${themeGet('colors.elevation.surface.default.[default]')};
  position: relative;
  display: flex;
  width: 100%;
  height: 100%;
  pointer-events: none;
  background-color: var(--background-color);
  border: 2px solid var(--border-color);
  min-height: 464px;
  ${sx}
`
const Arrow = styled(motion.div)<BoxWithIndicatorArrowProps>`
  --border-color: ${themeGet('colors.border.default')};
  --background-color: ${themeGet('colors.elevation.surface.default.[default]')};
  position: absolute;
  display: block;
  isolation: isolate;
  width: 0;
  height: 0;
  pointer-events: none;
  content: ' ';

  background-color: var(--background-color); /* Set caret's background color to white */
  border: 2px solid var(--border-color);
  border-radius: ${p => (p.arrowSize === 'sm' ? '2px' : p.arrowSize === 'md' ? '2px' : '3px')};
  width: ${p => (p.arrowSize === 'sm' ? '11px' : p.arrowSize === 'md' ? '24px' : '32px')};
  height: ${p => (p.arrowSize === 'sm' ? '11px' : p.arrowSize === 'md' ? '24px' : '32px')};
  // arrow direction
  transform: ${p =>
    p.side === 'left'
      ? 'translateY(-50%) translateX(50%) rotate(45deg)'
      : 'translateY(-50%) translateX(50%) rotate(-135deg)'};

  ${p =>
    p.arrowSize === 'sm' &&
    p.side === 'left' &&
    css`
      left: -12px;
      right: 100%;
    `};

  ${p =>
    p.arrowSize === 'md' &&
    p.side === 'left' &&
    css`
      left: -25px;
      right: 100%;
    `};

  ${p =>
    p.arrowSize === 'lg' &&
    p.side === 'left' &&
    css`
      left: -33px;
      right: 100%;
    `};

  ${p =>
    p.arrowSize === 'sm' &&
    p.side === 'right' &&
    css`
      /* left: 100%; */
      right: -2px;
    `};

  ${p =>
    p.arrowSize === 'md' &&
    p.side === 'right' &&
    css`
      /* left: 100%; */
      right: -2px;
    `};

  ${p =>
    p.arrowSize === 'lg' &&
    p.side === 'right' &&
    css`
      /* left: 100%; */
      right: -2px;
    `};

  &::before {
    content: ' ';
    display: block;
    position: absolute;
    background-color: var(--background-color);
    user-select: none;
    /* width: 35px;
    height: 42px;
    top: -10px;
    right: -12px; */
    width: ${p => (p.arrowSize === 'sm' ? '14px' : p.arrowSize === 'md' ? '35px' : '35px')};
    height: ${p => (p.arrowSize === 'sm' ? '17px' : p.arrowSize === 'md' ? '34px' : '44px')};
    top: ${p => (p.arrowSize === 'md' ? '-14px' : p.arrowSize === 'lg' ? '-16px' : '-10px')};
    /* right: -${p => (p.arrowSize === 'md' ? '0px' : '-12px')}; */
    left: 0px;
  }
`
BoxWithIndicator.displayName = 'BoxWithIndicator'
export default BoxWithIndicator
